import React, { createContext, useContext } from "react";
import { MDXProvider } from "@mdx-js/react";

import { GuideSection } from "./GuideSection";
import { GuideTable } from "./GuideTable";
import { TipCard } from "./TipCard";
import { CardWithHeader } from "./CardWithHeader";
import { BulletList, BulletListItem } from "./BulletList";
import { InlineAppCTA } from "./InlineAppCTA";
import { GuideImage } from "./GuideImage";
import { GuideImagePair } from "./GuideImagePair";
import { GuideCodeBlock } from "./GuideCodeBlock";
import { DspFlowDiagram } from "./DspFlowDiagram";
import { SensorPlacementDiagram } from "./SensorPlacementDiagram";
import { PrivacyFlowDiagram } from "./PrivacyFlowDiagram";

const PublishedSlugsContext = createContext(new Set());
export const CrownPriceContext = createContext(null);

export function PublishedSlugsProvider({ slugs, children }) {
  return (
    <PublishedSlugsContext.Provider value={slugs}>
      {children}
    </PublishedSlugsContext.Provider>
  );
}

export function CrownPriceProvider({ price, children }) {
  return (
    <CrownPriceContext.Provider value={price}>
      {children}
    </CrownPriceContext.Provider>
  );
}

function CrownPrice() {
  const price = useContext(CrownPriceContext);
  if (!price) return <span>see pricing</span>;
  const num = Math.round(parseFloat(price));
  const formatted = `$${num.toLocaleString("en-US")}`;
  return <span>{formatted}</span>;
}

// External domains that should receive dofollow links
const DOFOLLOW_DOMAINS = [
  "tinylog.app",
];

function GuideLink({ href, children, ...props }) {
  const publishedSlugs = useContext(PublishedSlugsContext);
  const isExternal = href && (href.startsWith("http://") || href.startsWith("https://"));
  if (isExternal) {
    const isDofollow = DOFOLLOW_DOMAINS.some((d) => href.includes(d));
    return (
      <a
        href={href}
        target="_blank"
        rel={isDofollow ? undefined : "nofollow noreferrer"}
        {...props}
      >
        {children}
      </a>
    );
  }

  // For /guides/{slug} links, check published status and prevent self-links
  if (href && href.startsWith("/guides/")) {
    const targetSlug = href.replace("/guides/", "").replace(/\/$/, "");
    const currentPath = typeof window !== "undefined" ? window.location.pathname : "";
    const isSelfLink = currentPath === `/guides/${targetSlug}` || currentPath === `/guides/${targetSlug}/`;
    if (isSelfLink) {
      return <span {...props}>{children}</span>;
    }
    if (publishedSlugs.size > 0 && !publishedSlugs.has(targetSlug)) {
      return <span {...props}>{children}</span>;
    }
  }

  return <a href={href} {...props}>{children}</a>;
}

const guideComponents = {
  a: GuideLink,
  pre: GuideCodeBlock,
  GuideSection,
  GuideTable,
  TipCard,
  CardWithHeader,
  BulletList,
  BulletListItem,
  InlineAppCTA,
  GuideImage,
  GuideImagePair,
  DspFlowDiagram,
  SensorPlacementDiagram,
  PrivacyFlowDiagram,
  CrownPrice
};

export function GuideMdxProvider({ children }) {
  return <MDXProvider components={guideComponents}>{children}</MDXProvider>;
}
